import { DataType, FormDialogMode, SelectOption, useNotify } from '@globaleris/ui.library'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumTipoAdhesionCFlotante } from '../../../../api'
import { enumToSelectOptionArray } from '../../../../helpers'
import { missingFieldsFormWithRequiredText } from '../../../../helpers/validateFormHelper'
import { FormDialogDefinitionProps, useFormDialog } from '../../../../hooks'
import { EnumFloatingConceptsTabs } from '../../floatingConcepts.model'

interface FloatingConceptsFormDialogProps {
  activeTab: EnumFloatingConceptsTabs
  formMode: FormDialogMode
  open: boolean
  onClose: (formData: any) => void
  inputData?: any
  settlementGroupOptionsList?: SelectOption[]
}

interface FormDependenciesProps {
  formState: any
  amountError: boolean
}

export interface FloatingConceptsFormDialog {
  conceptName: string
  adhesionType: EnumTipoAdhesionCFlotante
  startDate: Date
  endDate: Date
  quantity: number
  price: number
  amount: number
  conceptDetail: string
  settlementGroupId: number
}

export const FloatingConceptsFormDialog: React.FC<FloatingConceptsFormDialogProps> = ({
  activeTab,
  settlementGroupOptionsList,
  formMode,
  open,
  onClose,
  inputData
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'floatingConcepts'])
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()
  const { info, error } = useNotify()

  const [editFloatingConceptFormDependencies, setEditFloatingConceptFormDependencies] =
    useState<FormDependenciesProps>({} as FormDependenciesProps)

  // const isFloatingConceptsTab = activeTab === EnumFloatingConceptsTabs.floatingConcepts
  const isBalanceActionsTab = activeTab === EnumFloatingConceptsTabs.balanceActions
  // const isRightsTab = activeTab === EnumFloatingConceptsTabs.rights

  // #region Handlers Starts
  const onFormCloseHandler = async (formData: any) => {
    if (formData) {
      if (!formData.price || !formData.quantity) {
        error(
          !formData.price
            ? t(
                'floatingConcepts:components.floatingConceptsFormDialog.notifications.priceZeroNotAllowed'
              )
            : t(
                'floatingConcepts:components.floatingConceptsFormDialog.notifications.quantityZeroNotAllowed'
              )
        )
        return
      }

      if (missingFieldsFormWithRequiredText(formDefinition?.sections!, formData)) {
        error(
          t('floatingConcepts:components.floatingConceptsFormDialog.notifications.incompleteForm')
        )
        return
      }
    }
    closeForm()
    onClose(formData)
  }
  // #endregion Handlers Ends

  // #region Form Definition Starts
  const formDefinition = React.useMemo<FormDialogDefinitionProps>(
    () => ({
      id: 'EDIT_VALUE',
      sections: [
        {
          inputs: [
            {
              name: 'conceptName',
              inputSize: isBalanceActionsTab ? 12 : undefined,
              label: t('floatingConcepts:dataTable.columns.description'),
              type: DataType.TEXT,
              required: true
            },
            {
              name: 'settlementGroupId',
              display: isBalanceActionsTab,
              readOnly: isBalanceActionsTab,
              label: t(
                'floatingConcepts:components.floatingConceptsDefaultTab.searchForm.inputs.settlementGroup'
              ),
              type: DataType.SELECT,
              selectOptions: settlementGroupOptionsList ?? []
            },
            {
              name: 'adhesionType',
              readOnly: isBalanceActionsTab,
              label: t('floatingConcepts:customForm.inputs.adhesion'),
              type: DataType.SELECT,
              selectOptions: enumToSelectOptionArray(
                EnumTipoAdhesionCFlotante,
                'enums:EnumTipoAdhesionCFlotante'
              )
            },
            {
              name: 'startDate',
              label: t('floatingConcepts:customForm.inputs.initDate'),
              type: DataType.DATE,
              required: true
            },
            {
              name: 'endDate',
              label: t('floatingConcepts:customForm.inputs.endDate'),
              type: DataType.DATE,
              required: true
            },
            {
              name: 'quantity',
              label: t('floatingConcepts:customForm.inputs.quantity'),
              type: DataType.NUMBER,
              customDecimalScale: 8,
              inputSize: 4
            },
            {
              name: 'price',
              label: t('floatingConcepts:customForm.inputs.unitPrice'),
              type: DataType.NUMBER,
              customDecimalScale: 8,
              inputSize: 4
            },
            {
              name: 'amount',
              label: t('floatingConcepts:customForm.inputs.amount'),
              type: DataType.NUMBER,
              customDecimalScale: 8,
              inputSize: 4,
              errorState: editFloatingConceptFormDependencies.amountError,
              required: true
            },
            {
              name: 'conceptDetail',
              label: t('floatingConcepts:dataTable.columns.conceptDetail'),
              type: DataType.TEXT
            }
          ]
        }
      ]
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editFloatingConceptFormDependencies]
  )
  // #endregion Form Definition Ends

  // #region Hooks Starts
  useEffect(() => {
    if (open) {
      openForm({
        definition: formDefinition,
        title:
          formMode === FormDialogMode.EDIT
            ? t('floatingConcepts:components.floatingConceptsFormDialog.forms.edit.title')
            : t('floatingConcepts:components.floatingConceptsFormDialog.forms.add.title'),
        mode: formMode,
        isWaiting: false,
        ...(formMode === FormDialogMode.EDIT && { inputData: { ...inputData } }),
        onClose: onFormCloseHandler
      })

      setEditFloatingConceptFormDependencies({
        ...editFloatingConceptFormDependencies,
        formState: inputData,
        amountError: false
      })
    }
  }, [open])

  useEffect(() => {
    updateFormDefinition(formDefinition, editFloatingConceptFormDependencies.formState)
  }, [formDefinition])
  // #endregion Hooks Ends

  return (
    <>
      <CustomFormDialog {...formProps} />
    </>
  )
}
